<template>
  <div :class="basketItemClasses">
    <div class="basket-item__details">
      <img
        :src="getItemImage(item)"
        class="basket-item__image"
        @error="handleImageError" />
      <div class="basket-item__info">
        <div class="basket-item__name">
          <v-tooltip top :disabled="!isNameTruncated">
            <template v-slot:activator="{ on, attrs }">
              <span
                ref="nameSpan"
                class="basket-item__base_name"
                v-bind="attrs"
                v-on="on">
                {{ getItemName(item) }}
              </span>
            </template>
            <span>{{ getItemName(item) }}</span>
          </v-tooltip>
          <span v-if="shouldShowCoinAmount(item)">
            {{ getCoinAmount(item) }}
            <CoinIcon v-if="shouldShowCoinAmount(item)" :size="12" />
          </span>
        </div>
        <div class="basket-item__price">
          <template v-if="isCoinsVariant">
            {{ coinsPerCurrency }}
            <CoinIcon :size="13" />
            / 1{{ coinCurrencyLabel }}
          </template>
          <template v-else>
            {{ formatPrice(getItemUnitPrice(item)) }}
            {{ getItemCurrency(item) }}
          </template>
        </div>
      </div>
    </div>
    <div class="basket-item__actions">
      <div class="basket-item__quantity" v-if="!isPromotion">
        <template v-if="isCoinsVariant">
          <span class="basket-item__quantity-value">{{ item.quantity }}</span>
        </template>
        <template v-else>
          <v-btn
            icon
            x-small
            class="basket-item__quantity-btn"
            :disabled="isCartLoading"
            @click="decrement(item)">
            <v-icon>mdi-minus</v-icon>
          </v-btn>
          <span class="basket-item__quantity-value">{{ item.quantity }}</span>
          <v-btn
            icon
            x-small
            class="basket-item__quantity-btn"
            :disabled="isCartLoading"
            @click="increment(item)">
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </template>
      </div>
      <div v-else class="basket-item__quantity-placeholder"></div>
      <div class="basket-item__total">
        <template v-if="isCoinsVariant">
          <v-text-field
            v-model.number="coinTotalInput"
            type="number"
            step="0.01"
            min="0"
            hide-details
            dense
            outlined
            rounded
            :suffix="coinCurrencyLabel"
            :disabled="isCartLoading"
            @input="handleCoinTotalInput" />
        </template>
        <template v-else>
          <span>
            {{ formatPrice(getItemTotal(item)) }}
            {{ getItemCurrency(item) }}
          </span>
        </template>
        <span
          v-if="getReduction(item)"
          class="basket-item__strikethrough_price">
          {{ getPromotionOriginalPriceTotal(item) }}
          {{ getItemCurrency(item) }}
        </span>
      </div>
      <v-btn
        icon
        color="error"
        class="basket-item__remove"
        small
        :disabled="isCartLoading"
        @click="remove(item)">
        <v-icon size="18">mdi-cart-off</v-icon>
      </v-btn>
    </div>
    <PromotionElementSelectSection
      v-if="isPromotion"
      :promotion="item.promotion"
      :variants="variants"
      :cart-item="item" />
  </div>
</template>

<script>
import {
  calculatePromotionPriceFromSelections,
  getReducedPrice,
  roundPrice,
  sumSelectedVariantPrices,
} from '@/utils/price'
import {
  calculatePromotionPrice,
  isFixedPricePromotion,
} from '@/utils/promotions'
import { getVariantCurrency, getVariantPrice } from '@/utils/variant'
import { COIN_TYPES } from '@/constants/coins'
import PromotionElementSelectSection from './PromotionElementSelectSection.vue'
import { mapGetters } from 'vuex'
import CoinIcon from '@/components/CoinIcon.vue'

export default {
  name: 'BasketItem',
  components: {
    CoinIcon,
    PromotionElementSelectSection,
  },
  props: {
    item: { type: Object, required: true },
    variants: { type: Array, default: () => [] },
    arenaCurrency: { type: Object, default: () => ({}) },
    isHighlighted: { type: Boolean, default: false },
  },
  data: () => ({
    coinTotalInput: 0,
    isNameTruncated: false,
  }),
  computed: {
    ...mapGetters('cart', {
      isPromotionSelectionComplete: 'isPromotionSelectionComplete',
      isCartLoading: 'isCartLoading',
    }),
    isCoinsVariant() {
      if (this.item?.type !== 'variant') return false
      return this.item.variant?.attributes?.type === COIN_TYPES.coins
    },
    isPassVariant() {
      if (this.item?.type !== 'variant') return false
      return this.item.variant?.attributes?.hasOwnProperty('weekdays')
    },
    coinsPerCurrency() {
      const coins = Number(this.arenaCurrency?.coins_per_currency)
      return Number.isFinite(coins) && coins > 0 ? coins : 0
    },
    coinCurrencyLabel() {
      return (
        this.arenaCurrency?.symbol ||
        this.arenaCurrency?.code ||
        this.getItemCurrency(this.item) ||
        ''
      )
    },
    isPromotion() {
      return this.item?.type === 'promotion'
    },
    basketItemClasses() {
      const classes = ['basket-item']
      if (
        this.isPromotion &&
        !this.isPromotionSelectionComplete(this.item.promotion.id)
      ) {
        classes.push('basket-item--promotion-error')
      }
      if (this.isHighlighted) {
        classes.push('basket-item--highlighted')
      }
      return classes
    },
  },
  watch: {
    'item.quantity': {
      immediate: true,
      handler() {
        this.syncCoinTotalInput()
      },
    },
    coinsPerCurrency() {
      this.syncCoinTotalInput()
    },
    'item.variant.name'() {
      this.checkNameTruncation()
    },
    'item.promotion.name'() {
      this.checkNameTruncation()
    },
  },
  mounted() {
    this.checkNameTruncation()
  },
  updated() {
    this.$nextTick(() => {
      this.checkNameTruncation()
    })
  },
  methods: {
    checkNameTruncation() {
      this.$nextTick(() => {
        if (this.$refs.nameSpan) {
          const element = this.$refs.nameSpan
          this.isNameTruncated = element.scrollWidth > element.clientWidth
        }
      })
    },
    syncCoinTotalInput() {
      if (!this.isCoinsVariant || !this.coinsPerCurrency) {
        this.coinTotalInput = 0
        return
      }
      const quantity = Number(this.item?.quantity) || 0
      this.coinTotalInput = roundPrice(quantity / this.coinsPerCurrency)
    },
    emitVariantQuantity(payload) {
      if (this.isCoinsVariant) {
        const safeQuantity = Math.max(1, Number(payload.quantity) || 0)
        payload = { ...payload, quantity: safeQuantity }
      }
      this.$emit('update-variant-quantity', payload)
    },
    increment(item) {
      if (this.isCartLoading) return
      if (item.type === 'variant') {
        const nextQuantity = item.quantity + 1
        const availableStock = item.variant?.stock?.available ?? 0
        if (
          (!this.isCoinItem(item) || !this.isPassVariant) &&
          item.variant?.track_stock &&
          nextQuantity > availableStock
        ) {
          this.$store.dispatch('showSnackbar', {
            color: 'error',
            message: this.$t('purchaseV2.catalog.insufficientStock'),
            icon: 'error',
          })
          return
        }
        this.emitVariantQuantity({
          variant: item.variant,
          quantity: nextQuantity,
        })
      } else {
        if (item.quantity >= 1) return
        const nextQuantity = item.quantity + 1
        this.$emit('update-promotion-quantity', {
          promotion: item.promotion,
          quantity: nextQuantity,
        })
      }
    },
    decrement(item) {
      if (this.isCartLoading) return
      const nextQuantity = Math.max(0, item.quantity - 1)
      if (item.type === 'variant') {
        this.emitVariantQuantity({
          variant: item.variant,
          quantity: nextQuantity,
        })
      } else {
        this.$emit('update-promotion-quantity', {
          promotion: item.promotion,
          quantity: nextQuantity,
        })
      }
    },
    remove(item) {
      if (this.isCartLoading) return
      if (item.type === 'variant') {
        this.$emit('remove-variant', { variant: item.variant })
      } else {
        this.$emit('remove-promotion', { promotion: item.promotion })
      }
    },
    getItemImage(item) {
      return item.type === 'variant'
        ? item.variant?.image
        : item.promotion?.image
    },
    getItemName(item) {
      return item.type === 'variant' ? item.variant?.name : item.promotion?.name
    },
    shouldShowCoinAmount(item) {
      if (!this.isCoinItem(item)) return false
      const coinType = item.variant?.attributes?.type
      return coinType !== COIN_TYPES.coins
    },
    isCoinItem(item) {
      if (item.type !== 'variant') return false
      const coinType = item.variant?.attributes?.type
      return Object.values(COIN_TYPES).includes(coinType)
    },
    getCoinAmount(item) {
      return item.variant?.attributes?.amount
    },
    getItemUnitPrice(item) {
      if (item.type === 'variant') {
        return roundPrice(getVariantPrice(item.variant) ?? 0)
      }
      const isComplete = this.isPromotionSelectionComplete(item.promotion.id)
      return isComplete
        ? calculatePromotionPriceFromSelections(
            item.promotion,
            item.groupSelections,
            this.variants,
            getVariantPrice,
            this.arenaCurrency,
          )
        : this.getPromotionPrice(item.promotion)
    },
    getItemCurrency(item) {
      return item.type === 'variant'
        ? getVariantCurrency(item.variant) || ''
        : this.getPromotionCurrency(item.promotion)
    },
    getItemTotal(item) {
      return roundPrice(this.getItemUnitPrice(item) * item.quantity)
    },
    getReduction(item) {
      if (item.type === 'variant') return
      return item.promotion.discount_value
    },
    getPromotionOriginalPriceTotal(item) {
      if (item.type === 'variant') return
      const isComplete = this.isPromotionSelectionComplete(item.promotion.id)
      const basePrice = isComplete
        ? sumSelectedVariantPrices(
            item.promotion,
            item.groupSelections,
            this.variants,
            getVariantPrice,
            this.arenaCurrency,
          )
        : calculatePromotionPrice(
            item.promotion,
            this.variants,
            this.arenaCurrency,
          ) ?? 0
      return roundPrice(basePrice * item.quantity)
    },
    getPromotionPrice(promotion) {
      if (isFixedPricePromotion(promotion)) {
        return Number(promotion.discount_value)
      }

      const basePrice =
        calculatePromotionPrice(promotion, this.variants, this.arenaCurrency) ??
        0
      const basePriceRoundedString = basePrice.toFixed(2)
      const reducedPrice = getReducedPrice(
        basePriceRoundedString,
        promotion.discount_value,
      )
      return Number(reducedPrice)
    },
    getPromotionCurrency(promotion) {
      if (!promotion?.groups?.length) return ''
      const allVariantIds = promotion.groups
        .flatMap((group) => group.variants)
        .map((variant) => variant.arena_variant_id)
      const matchingVariant = this.variants.find((v) =>
        allVariantIds.includes(v.id?.toString()),
      )
      return getVariantCurrency(matchingVariant) || ''
    },
    formatPrice(value) {
      return Number(value).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },
    handleImageError(event) {
      event.target.style.display = 'none'
    },
    handleCoinTotalInput(value) {
      if (!this.isCoinsVariant || this.isCartLoading) return
      if (value === '' || value === null || value === undefined) {
        this.coinTotalInput = ''
        return
      }
      const numericValue = Number(value)
      if (!this.coinsPerCurrency || Number.isNaN(numericValue)) {
        return
      }
      const calculatedQuantity = Math.max(
        1,
        Math.round(numericValue * this.coinsPerCurrency),
      )
      this.emitVariantQuantity({
        variant: this.item.variant,
        quantity: calculatedQuantity,
      })
    },
  },
}
</script>

<style scoped lang="scss">
.basket-item {
  --basket-item-bg: #fff;
  --basket-item-border: #7f7f7f;
  --basket-item-text: #2b2b2b;
  --basket-item-text-muted: #4a4a4a;
  --basket-item-strikethrough: #312929;
  --basket-item-highlight-bg: #284b7633;
  --basket-item-highlight-shadow: rgba(0, 0, 0, 0.25);
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.25rem;
  border: 1px solid var(--basket-item-border);
  border-radius: 6px;
  padding: 0.5rem;
  background: var(--basket-item-bg);
  margin-left: 2px;

  &:last-child {
    margin-bottom: 5px;
  }
}

.theme--dark .basket-item {
  --basket-item-bg: var(--v-background-base, #1e1e1e);
  --basket-item-border: rgba(255, 255, 255, 0.12);
  --basket-item-text: rgba(255, 255, 255, 0.87);
  --basket-item-text-muted: rgba(255, 255, 255, 0.6);
  --basket-item-strikethrough: rgba(255, 255, 255, 0.5);
  --basket-item-highlight-bg: rgba(40, 75, 118, 0.35);
  --basket-item-highlight-shadow: rgba(0, 0, 0, 0.4);
}

.basket-item--promotion-error {
  border-color: #d74a4a;
}

.basket-item--highlighted {
  background-color: var(--basket-item-highlight-bg);
  box-shadow: -2px 0 2px var(--basket-item-highlight-shadow),
    2px 0 2px var(--basket-item-highlight-shadow),
    0 4px 2px var(--basket-item-highlight-shadow);
  transition: box-shadow 0.5s ease, background-color 0.5s ease;
}

.basket-item__image {
  width: 2.8125rem;
  height: 2.8125rem;
  object-fit: cover;
  border-radius: 12px;
}

.basket-item__info {
  min-width: 0;
  max-width: 7.5rem;
}

.basket-item__name {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  font-size: 0.75rem;
  font-weight: 600;
  max-width: 7.5rem;
  color: var(--basket-item-text);
  white-space: nowrap;
}

.basket-item__base_name {
  max-width: 8rem;
  overflow: hidden;
  text-overflow: ellipsis;
}

.basket-item__details {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.basket-item__actions {
  display: flex;
  align-items: center;
  flex-basis: 50%;
}

.basket-item__price {
  font-size: 0.875rem;
  color: var(--basket-item-text-muted);
}

.basket-item__quantity {
  display: flex;
  align-items: center;
}

.basket-item__quantity-placeholder {
  min-width: 4.5rem;
}

.basket-item__quantity-btn {
  background-color: var(--v-primary-base) !important;
  color: #fff !important;
}

.basket-item__quantity-value {
  min-width: 1.75rem;
  text-align: center;
  font-size: 1rem;
  font-weight: 600;
  color: var(--basket-item-text);
}

.basket-item__total {
  font-size: 1.125rem;
  display: flex;
  flex-direction: column;
  font-weight: 700;
  line-height: normal;
  color: var(--basket-item-text);
  padding: 0 0.25rem 0 0.5rem;
}

.basket-item__strikethrough_price {
  font-size: 0.75rem;
  line-height: normal;
  align-self: flex-start;
  color: var(--basket-item-strikethrough);
  text-decoration: none;
  position: relative;

  &:before {
    content: ' ';
    color: rgba(red, 0.5);
    display: block;
    width: 100%;
    border-top: 1px solid rgba(red, 0.8);
    height: 0.5rem;
    position: absolute;
    bottom: 0;
    left: 0;
    transform: rotate(-20deg);
  }
}

.basket-item__remove {
  margin-left: auto;
}

@media screen and (max-width: 1500px) {
  .basket-item__actions {
    width: 100%;
    justify-content: space-between;
    flex-basis: unset;
  }

  .basket-item__name {
    max-width: unset;
  }

  .basket-item__info {
    max-width: auto;
  }
}
</style>
